import React, { useRef, useState, useEffect } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { signup, login, db, auth, logout, googleSignIn, sendPasswordResetEmail, sendVerificationEmail, getEmailVerificationRedirectUrl, setDoc } from "../../base";
import "../auth/auth.css";
import {
  doc,
  getDoc, updateDoc
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import ContentModal from '../contentModal/ContentModal';
import CombinedTermsOfUse from '../legal/CombinedTermsOfUse';
import { Timestamp } from "firebase/firestore";
import { handleAnalyticsEvent } from "../../helpers/analyticsHandler";
import { activatePendingStandardTrial, getPendingStandardTrialFields } from '../../helpers/standardTrial';
import { getAcquisitionContext, recordAdminJourneyEvent } from '../../helpers/adminAnalytics';


const hasStartedStandardTrial = (data = {}) => Boolean(
  data?.standardTrialStartedAt ||
  data?.standardTrialEndsAt ||
  (data?.standardTrialStatus && data.standardTrialStatus !== 'not_started')
);

const hasPaidPlanAccess = (data = {}) => {
  const planName = data?.planName || data?.planType || data?.currentPlan;
  const hasPaidPlan = ['my_project_plan', 'standard_plan', 'pro_plan', 'My Project', 'Standard', 'Pro'].includes(planName);
  const hasPurchasedProject = Number(data?.extraProjectPurchased || 0) > 0;
  const hasActiveSubscription = data?.isSubscription && ['active', 'cancel_at_period_end'].includes(data?.subscriptionStatus);

  return hasPaidPlan || hasPurchasedProject || hasActiveSubscription;
};


function Auth({ closeSignup, closeUpload, setUserRole, handleLogOut, setCloseAccount, setLogin, handleAccept, handleDecline,
  isLoginOut, setIsLoginOut, setProjectName, loginSet, toJson, clearCanvas, termsAccepted, sendWelcomeEmail, setIsGoogleProcessing, showEmailVerificationPendingModal, resetActiveProjectAfterAuth, currentUser: currentUserProp, authContext = null }) {
  const currentUser = currentUserProp;
  const [error, setError] = useState(null);
  const [userRegion, setUserRegion] = useState(null);
  const [isProfileFormVisible, setIsProfileFormVisible] = useState(false);
  const isGuestUser = Boolean(currentUser?.email?.startsWith("guest_"));
  const hasRealAuthenticatedUser = Boolean(currentUser?.uid && !isGuestUser);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false); // Add loading state for signup
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Add loading state for login
  const [isGoogleLoading, setIsGoogleLoading] = useState(false); // Add loading state for Google auth
  // Note: isGoogleProcessing is now managed by App.js and passed as prop setIsGoogleProcessing
  const { t, i18n } = useTranslation(["translation"]);



  // show terms

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState(null);
  const [termsViewed, setTermsViewed] = useState(false);

  // Add cleanup tracking to prevent memory leaks
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      isMountedRef.current = false;
      document.body.style.overflow = "auto"; // Reset body overflow
    };
  }, []);

  const handleOpenTermsModal = () => {
    setModalTitle(t('Terms of Use'));
    setModalContent(<CombinedTermsOfUse />);
    setTermsViewed(true); // Set the checkbox to checked when the terms are viewed
    setModalOpen(true);
  };

  const emailRef = useRef();
  const passwordRef = useRef();
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const companyRef = useRef();

  useEffect(() => {
    if (isProfileFormVisible) {
      // Perform any necessary actions when the profile form becomes visible

    }
  }, [isProfileFormVisible]);

  const switchAuthMode = (nextMode) => {
    if (!isMountedRef.current) return;
    setIsPasswordRecovery(false);
    setError(null);
    setLogin(nextMode === "login");
  };

  const finalizeAuthSuccess = () => {
    if (typeof resetActiveProjectAfterAuth === "function") {
      resetActiveProjectAfterAuth();
      return;
    }

    if (typeof setProjectName === "function") {
      setProjectName("");
    }

    if (typeof clearCanvas === "function") {
      clearCanvas();
    }
  };
  // Infer region locally to avoid third-party geo API failures/noise in client logs.
  useEffect(() => {
    const language = navigator?.language || navigator?.languages?.[0] || "";
    const localeRegion = language.includes("-") ? language.split("-")[1] : null;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fallbackRegion = timeZone ? timeZone.split("/").pop()?.replace(/_/g, " ") : null;

    setUserRegion(localeRegion || fallbackRegion || "Unknown");
  }, []);


  // const actionCodeSettings = {
  //   // URL you want to redirect back to. The domain (www.yourdomain.com) for this
  //   // URL must be in the authorized domains list in the Firebase Console.
  //   url: 'http://localhost:3000/?emailVerified=true', // The URL to redirect to after the user clicks the verification link in the email
  //   handleCodeInApp: false, // This should be false unless you are handling the code in the app
  // };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const shouldStartStandardTrial = authContext?.intent === 'trial';

  const buildUserData = ({ email, firstName, lastName, userName, companyName, startStandardTrial = false }) => {
    const baseUserData = {
      role: "user",
      email: email,
      emailConfirmed: false,
      firstName: firstName,
      lastName: lastName,
      userName: userName,
      companyName: companyName,
      currentPlan: "free",
      planName: "free",
      planType: "free",
      subscriptionStatus: "inactive",
      isSubscription: false,
      standardTrialStatus: "not_started",
      standardTrialProjectLimit: 0,
      standardTrialWatermarkedDownloads: false,
      lastInteractionDate: Timestamp.now(),
      weeklyInteractionCount: 0,
      assistantInteractionCount: 0,
      lastLogginDate: Timestamp.now(),
      userRegion: userRegion,
      createdAt: Timestamp.now(),
      signupSource: authContext?.source || 'direct_signup',
      acquisition: getAcquisitionContext(),
    };

    return startStandardTrial
      ? { ...baseUserData, ...getPendingStandardTrialFields(authContext?.source || 'pricing') }
      : baseUserData;
  };

  const ensureUserDoc = async (uid, userData, attempts = 3) => {
    const userDocRef = doc(db, "users", uid);

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        await setDoc(userDocRef, userData, { merge: true });
        return;
      } catch (error) {
        if (attempt === attempts) {
          throw error;
        }
        await sleep(250 * attempt);
      }
    }
  };

  const syncEmailConfirmationForUser = async (user, fallbackEmail = "") => {
    if (!user?.uid) return;

    const userDocRef = doc(db, "users", user.uid);
    const updateData = {
      emailConfirmed: Boolean(user.emailVerified),
      emailVerificationSyncedAt: Timestamp.now(),
    };

    const normalizedEmail = (user.email || fallbackEmail || "").trim();
    if (normalizedEmail) {
      updateData.email = normalizedEmail;
    }

    await setDoc(userDocRef, updateData, { merge: true });
  };

  const maybeActivateRequestedTrial = async (uid, existingData = null) => {
    if (!uid) {
      return false;
    }

    const userDocRef = doc(db, 'users', uid);
    let data = existingData;

    if (!data) {
      const snapshot = await getDoc(userDocRef);
      data = snapshot.exists() ? snapshot.data() : null;
    }

    if (data?.standardTrialStatus === 'pending_verification') {
      const activated = await activatePendingStandardTrial(db, auth.currentUser);
      if (activated) {
        await recordAdminJourneyEvent(db, { eventName: 'trial_activated', userId: uid, email: auth.currentUser?.email, source: data.standardTrialSource || authContext?.source || 'verified_login' });
        handleAnalyticsEvent('trial_activated', 'trial', 'verified_login');
      }
      return activated;
    }

    if (authContext?.intent !== 'trial' || hasStartedStandardTrial(data) || hasPaidPlanAccess(data)) {
      return false;
    }

    await setDoc(userDocRef, getPendingStandardTrialFields(authContext?.source || 'pricing'), { merge: true });
    handleAnalyticsEvent('trial_requested', 'trial', 'existing_account');
    const activated = await activatePendingStandardTrial(db, auth.currentUser);
    if (activated) {
      await recordAdminJourneyEvent(db, { eventName: 'trial_activated', userId: uid, email: auth.currentUser?.email, source: authContext?.source || 'existing_account' });
      handleAnalyticsEvent('trial_activated', 'trial', 'verified_login');
    }
    return activated;
  };

  async function handleSignup() {
    if (!isMountedRef.current) return;
    setIsLoginOut(false);
    setIsSigningUp(true); // Start loading

    let missingFields = [];
    if (!emailRef.current.value) missingFields.push(t("auth-emailLabel"));
    if (!passwordRef.current.value) missingFields.push(t("auth-passwordLabel"));
    if (!firstNameRef.current.value) missingFields.push(t("auth-firstNameLabel"));
    if (!lastNameRef.current.value) missingFields.push(t("auth-lastNameLabel"));

    if (missingFields.length > 0) {
        let message = t("auth-pleaseEnter") + " " + missingFields.map(field =>
            field.charAt(0).toUpperCase() + field.slice(1)
        ).join(", ");
        alert(message);
        setIsSigningUp(false); // Stop loading
        return;
    }

    const email = emailRef.current.value;
    const password = passwordRef.current.value;
    const firstName = firstNameRef.current?.value || "";
    const lastName = lastNameRef.current?.value || "";
    const userName = `${firstName} ${lastName}`;
    const companyName = companyRef.current?.value || null;

    try {
        const userCredential = await signup(email, password);
        const userId = userCredential.user.uid;

        const userData = buildUserData({
          email,
          firstName,
          lastName,
          userName,
          companyName,
          startStandardTrial: shouldStartStandardTrial,
        });

        await ensureUserDoc(userId, userData);
        await recordAdminJourneyEvent(db, {
          eventName: 'account_created', userId, email,
          source: authContext?.source || 'direct_signup',
          details: { method: 'email', accountType: shouldStartStandardTrial ? 'trial_pending_verification' : 'free' },
        });
        if (shouldStartStandardTrial) {
          await recordAdminJourneyEvent(db, { eventName: 'trial_requested', userId, email, source: authContext?.source || 'signup' });
          handleAnalyticsEvent('trial_requested', 'trial', authContext?.source || 'signup');
        }

        const actionCodeSettings = {
          url: getEmailVerificationRedirectUrl(),
          handleCodeInApp: true,
        };

        try {
          await sendVerificationEmail(actionCodeSettings);
        } catch (verificationError) {
          console.error("Verification email send failed:", verificationError);
        }

        // Send welcome email for regular signups too
        try {
            if (typeof sendWelcomeEmail === 'function') {
                await sendWelcomeEmail({ email: email, name: userName }, i18n.language);
            }
        } catch (emailErr) {
            console.error('Error sending welcome email for regular signup:', emailErr);
        }

        // **✅ Detect if user was a guest before signing up**
        const wasGuest = sessionStorage.getItem("wasGuest");

        if (wasGuest === "true") {
            handleAnalyticsEvent("guest_to_signup", "Signup", "Guest user signed up");
            sessionStorage.removeItem("wasGuest"); // **✅ Clear guest session flag**
        } else {
            handleAnalyticsEvent("direct_signup", "Signup", "User signed up directly");
        }

        finalizeAuthSuccess();

        // Close the auth modal before opening the verification modal.
        // React-Bootstrap modal teardown can conflict when both modals overlap during signup.
        if (isMountedRef.current) {
          closeSignup(false);
        }

        // ✅ Show email verification pending modal instead of alert
        if (typeof showEmailVerificationPendingModal === 'function') {
            showEmailVerificationPendingModal(email, userName);
        } else {
            alert(t("auth-emailNeedConfirm")); // Fallback to alert if function not available
        }
    } catch (signupError) {
        console.error("Signup Error:", signupError);
        if (signupError.code === "auth/weak-password") {
            alert(t("auth-weakPassword"));
        } else if (signupError.code === "auth/email-already-in-use") {
            alert(t("auth-accountExists"));
        } else {
            alert(t("auth-signupError"));
        }
    } finally {
        if (isMountedRef.current) {
          setIsSigningUp(false); // Stop loading regardless of success or failure
        }
    }
}


  async function handleLogin() {
    if (!isMountedRef.current) return;
    setIsLoginOut(false);
    setIsLoggingIn(true); // Start loading

    try {
      await login(emailRef.current.value, passwordRef.current.value);

      // Ensure currentUser is defined after login
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently logged in.');
      }

      if (!currentUser.emailVerified) {
        const displayName = currentUser.displayName || "";
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnapshot = await getDoc(userDocRef);
        const userData = userDocSnapshot.exists() ? userDocSnapshot.data() : null;
        const fallbackName = displayName || userData?.userName || userData?.firstName || currentUser.email || emailRef.current.value || "";

        if (isMountedRef.current) {
          closeSignup(false);
        }

        if (typeof showEmailVerificationPendingModal === 'function') {
          showEmailVerificationPendingModal(currentUser.email || emailRef.current.value || "", fallbackName);
        } else {
          alert(t("auth-emailNeedConfirm"));
        }

        return;
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        const displayName = currentUser.displayName || "";
        const names = displayName.split(' ').filter(Boolean);
        const firstName = names[0] || "";
        const lastName = names.length > 1 ? names[names.length - 1] : "";
        const fallbackUserName = [firstName, lastName].filter(Boolean).join(' ').trim() || displayName || "User";

        const fallbackUserData = buildUserData({
          email: currentUser.email || emailRef.current.value || "",
          firstName,
          lastName,
          userName: fallbackUserName,
          companyName: "None",
          startStandardTrial: shouldStartStandardTrial,
        });

        await ensureUserDoc(currentUser.uid, fallbackUserData);
      } else {
        await maybeActivateRequestedTrial(currentUser.uid, userDocSnapshot.data());
      }

      // Update the last login date
      await updateDoc(userDocRef, {
        lastLogginDate: Timestamp.now(),
        emailConfirmed: Boolean(currentUser.emailVerified),
        ...(currentUser.email ? { email: currentUser.email } : {}),
        emailVerificationSyncedAt: Timestamp.now(),
      });

      finalizeAuthSuccess();
      window.history.pushState({}, document.title, "/");

      if (isMountedRef.current) {
        closeSignup(false);
      }

    } catch (error) {
      console.error("Login Error: ", error); // Log the error for debugging
      alert(t("auth-invalidCredentials")); // You can modify this to show a more specific error message
    } finally {
      if (isMountedRef.current) {
        setIsLoggingIn(false); // Stop loading regardless of success or failure
      }
    }
  }

  async function handleGoogleSignIn() {
    if (!isMountedRef.current) return; // Prevent execution if component unmounted

    setIsLoginOut(false);
    if (isMountedRef.current) setIsGoogleLoading(true); // Start loading for popup

    try {
      await googleSignIn(); // Perform Google authentication
      
      // After Google popup closes, show processing state
      if (isMountedRef.current) setIsGoogleLoading(false); // Stop popup loading
      if (setIsGoogleProcessing) setIsGoogleProcessing(true); // Start post-processing loading (App-level)
      
      const currentUser = auth.currentUser;

      // Define the user document reference
      const userDocRef = doc(db, "users", currentUser.uid);

      // Check if the user document already exists
      const docSnap = await getDoc(userDocRef);

      if (!docSnap.exists()) {
        // User does not exist, create new user data
        const userName = currentUser.displayName || "";
        const email = currentUser.email || "";

        const names = userName.split(' ');
        const firstName = names[0];
        const lastName = names.length > 1 ? names[names.length - 1] : '';

        const userData = {
          ...buildUserData({
            email,
            firstName,
            lastName,
            userName,
            companyName: "None",
            startStandardTrial: shouldStartStandardTrial,
          }),
          emailConfirmed: Boolean(currentUser.emailVerified),
          createdAt: Timestamp.now(),
          signupSource: authContext?.source || 'google_signup',
          acquisition: getAcquisitionContext(),
        };

        await setDoc(userDocRef, userData);
        await recordAdminJourneyEvent(db, {
          eventName: 'account_created', userId: currentUser.uid, email,
          source: authContext?.source || 'google_signup',
          details: { method: 'google', accountType: shouldStartStandardTrial ? 'trial' : 'free' },
        });
        if (shouldStartStandardTrial) {
          await recordAdminJourneyEvent(db, { eventName: 'trial_requested', userId: currentUser.uid, email, source: authContext?.source || 'google_signup' });
        }
        if (shouldStartStandardTrial) handleAnalyticsEvent('trial_requested', 'trial', authContext?.source || 'google_signup');
        if (shouldStartStandardTrial) await maybeActivateRequestedTrial(currentUser.uid, userData);

        // Trigger welcome + admin notification email for new Google signups
        try {
          if (typeof sendWelcomeEmail === 'function') {
            await sendWelcomeEmail({ email: email, name: userName }, i18n.language);
          }
        } catch (emailErr) {
          console.error('Error sending welcome email for Google signup:', emailErr);
        }
      } else {
        // User already exists, you can update specific fields if necessary
        // For example, updating the last interaction date:
        await maybeActivateRequestedTrial(currentUser.uid, docSnap.data());
        await updateDoc(userDocRef, {
          lastLogginDate: Timestamp.now(),
          emailConfirmed: Boolean(currentUser.emailVerified),
          ...(currentUser.email ? { email: currentUser.email } : {}),
          emailVerificationSyncedAt: Timestamp.now(),
        });
      }

      await syncEmailConfirmationForUser(currentUser);

      if (isMountedRef.current) {
        finalizeAuthSuccess();
        closeSignup(false);
        closeUpload(true);
      }
    } catch (error) {
      if (isMountedRef.current) setIsGoogleLoading(false); // Stop popup loading on error
      if (setIsGoogleProcessing) setIsGoogleProcessing(false); // Stop processing loading on error
      
      if (error.code === "auth/popup-closed-by-user") {
        alert(t("auth-googleSignInCanceled"));
      } else {
        alert(t("auth-googleSignInError"));
      }
    } finally {
      // Always stop processing loading regardless of success or failure
      if (setIsGoogleProcessing) setIsGoogleProcessing(false);
    }
  }



  async function handlePasswordRecovery() {
    const email = emailRef.current.value;

    if (!email) {
      alert(t("auth-enterEmail"));
      return;
    }

    try {
      await sendPasswordResetEmail(email); // call the imported function
      alert(t("auth-passwordResetSent"));
      setIsPasswordRecovery(false);
    } catch (error) {
      console.error("Password Reset Error:", error);
      alert(t("auth-passwordResetError"));
    }
  }









  return (
    <>
      <Modal
        show={true}
        onHide={() => {
          closeSignup(false);
          document.body.style.overflow = "auto";
        }}
        dialogClassName="auth-modal"
        contentClassName="auth-modal-content"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title> {hasRealAuthenticatedUser && isLoginOut ? t("auth-logoutTitle") : (loginSet ? t("auth-loginTitle") : t("auth-signupTitle"))}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {hasRealAuthenticatedUser ? (
          <Button
            variant="secondary"
            onClick={handleLogOut}
            className="logout-button"
          >
            {t("auth-logout")}
          </Button>
        ) : (
          <Form>
            {loginSet && !isPasswordRecovery && (
              <>
                <Form.Group controlId="formEmail">
                  <Form.Label>{t("auth-emailLabel")}</Form.Label>
                  <Form.Control
                    ref={emailRef}
                    type="email"
                    placeholder={t("auth-emailPlaceholder")}
                    autoComplete="email"
                  />
                </Form.Group>
                <Form.Group controlId="formPassword">
                  <Form.Label>{t("auth-passwordLabel")}</Form.Label>
                  <Form.Control
                    ref={passwordRef}
                    type="password"
                    placeholder={t("auth-passwordPlaceholder")}
                    autoComplete="current-password"
                  />
                </Form.Group>
                <Button variant="link" onClick={() => setIsPasswordRecovery(true)}>
                  {t("auth-forgotPassword")}
                </Button>
              </>
            )}
            {!loginSet && (
              <>
                <div className={`infoBubble ${authContext?.intent === 'trial' ? 'infoBubble--trial' : ''}`}>
                  <h3>
                    {authContext?.intent === 'trial'
                      ? t('auth-startTrialHeadline', { defaultValue: 'Start your 7-day Standard trial' })
                      : authContext?.intent === 'purchase'
                        ? t('auth-createAccountToPurchase', { defaultValue: 'Create your account to continue to checkout' })
                        : t("auth-freesignup")}
                  </h3>
                  <p className="infoBubble-copy">
                    {authContext?.intent === 'trial'
                      ? t('auth-startTrialCopy', { defaultValue: 'No credit card is needed to start. Try the app on your own house for 7 days, with a watermark on trial projects until you upgrade.' })
                      : authContext?.intent === 'purchase'
                        ? t('auth-purchaseFlowCopy', { defaultValue: 'After signup and email confirmation, return to pricing to complete your purchase through Stripe.' })
                        : t('auth-defaultSignupCopy', { defaultValue: 'Create an account to save projects, start a free trial, or purchase a plan later.' })}
                  </p>
                </div>
                <Form.Group controlId="formEmail">
                  <Form.Label>{t("auth-emailLabel")}</Form.Label>
                  <Form.Control
                    ref={emailRef}
                    type="email"
                    placeholder={t("auth-emailPlaceholder")}
                    autoComplete="email"
                  />
                </Form.Group>
                <Form.Group controlId="formPassword">
                  <Form.Label>{t("auth-passwordLabel")}</Form.Label>
                  <Form.Control
                    ref={passwordRef}
                    type="password"
                    placeholder={t("auth-passwordPlaceholder")}
                    autoComplete="new-password"
                  />
                </Form.Group>
                {isProfileFormVisible || !loginSet && (
                  <>
                    <Form.Group controlId="formFirstName">
                      <Form.Label>{t("auth-firstNameLabel")}</Form.Label>
                      <Form.Control
                        ref={firstNameRef}
                        type="text"
                        placeholder={t("auth-firstNamePlaceholder")}
                      />
                    </Form.Group>
                    <Form.Group controlId="formLastName">
                      <Form.Label>{t("auth-lastNameLabel")}</Form.Label>
                      <Form.Control
                        ref={lastNameRef}
                        type="text"
                        placeholder={t("auth-lastNamePlaceholder")}
                      />
                    </Form.Group>
                    <Form.Group controlId="formCompanyName">
                      <Form.Label>{t("auth-companyNameLabel")}</Form.Label>
                      <Form.Control
                        ref={companyRef}
                        type="text"
                        placeholder={t("auth-companyNamePlaceholder")}
                      />
                    </Form.Group>
                  </>
                )}
                <ContentModal
                  show={modalOpen}
                  title={modalTitle}
                  content={modalContent}
                  handleAccept={() => {
                    // Accept terms/cookies logic
                    setModalOpen(false);
                  }}
                  handleDecline={() => {
                    // Decline terms/cookies logic
                    setModalOpen(false);
                  }}
                />

              </>

            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!hasRealAuthenticatedUser && loginSet && !isPasswordRecovery && (
          <>
            <Button
              variant="primary"
              disabled={hasRealAuthenticatedUser || isLoggingIn}
              onClick={handleLogin}
            >
              {isLoggingIn && (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="auth-button-spinner"
                  />
                  <span className="auth-loading-text">{t("auth-loggingIn") || "Logging in..."}</span>
                </>
              )}
              {!isLoggingIn && t("auth-login")}
            </Button>
            <Button
              variant="primary"
              disabled={hasRealAuthenticatedUser || isLoggingIn || isGoogleLoading}
              onClick={handleGoogleSignIn}
            >
              {isGoogleLoading && (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="auth-button-spinner"
                  />
                  <span className="auth-loading-text">{t("auth-googleSigningIn") || "Signing in..."}</span>
                </>
              )}
              {!isGoogleLoading && t("auth-googleLogin")}
            </Button>
          </>
        )}
        {!hasRealAuthenticatedUser && !loginSet && (
          <>
            <Button
              variant="primary"
              disabled={isSigningUp}
              onClick={handleSignup}
            >
              {isSigningUp && (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="auth-button-spinner"
                  />
                  <span className="auth-loading-text">{t("auth-signingUp") || "Creating account..."}</span>
                </>
              )}
              {!isSigningUp && t("auth-signup")}
            </Button>

            <Button
              variant="primary"
              disabled={hasRealAuthenticatedUser || isSigningUp || isGoogleLoading}
              onClick={handleGoogleSignIn}
            >
              {isGoogleLoading && (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="auth-button-spinner"
                  />
                  <span className="auth-loading-text">{t("auth-googleSigningUp") || "Signing up..."}</span>
                </>
              )}
              {!isGoogleLoading && t("auth-googleSignUp")}
            </Button>

          </>
        )}

        {isPasswordRecovery && (
          <Form>
            <Form.Group controlId="formEmail">
              <Form.Label>{t("auth-emailLabel")}</Form.Label>
              <Form.Control
                ref={emailRef}
                type="email"
                placeholder={t("auth-emailPlaceholder")}
              />
            </Form.Group>
            <Button className="emailToRecover" variant="primary" onClick={handlePasswordRecovery}>
              {t("auth-resetPassword")}
            </Button>
            <Button variant="link" onClick={() => setIsPasswordRecovery(false)}>
              {t("auth-cancel")}
            </Button>
          </Form>
        )}

        {!hasRealAuthenticatedUser && !isPasswordRecovery && (
          <div className="auth-mode-switch">
            <span>
              {loginSet ? `${t("auth-signupTitle")} ?` : `${t("auth-loginTitle")} ?`}
            </span>
            <Button
              variant="link"
              className="auth-mode-switch-button"
              onClick={() => switchAuthMode(loginSet ? "signup" : "login")}
            >
              {loginSet ? t("signup") : t("login")}
            </Button>
          </div>
        )}

      </Modal.Footer>
    </Modal>
  </>
  );
}

export default Auth;
